import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { GetPackageDetailsRequest } from 'ts/package/classes/GetPackageDetailsRequest';
import { getPackageDetails } from 'services/package';
import { OutboundModel, PackageDetailsResponse } from 'ts/package/interfaces/PackageDetails';
import PackageDetails from './PackageDetails';
import AdditionalDetails from './AdditionalDetails';
import HotelDetails from './HotelDetails';
import SummaryDetails from './SummaryDetails';
import DetailsPackageSkeleton from './DetailsPackageSkeleton';
import ExpiredToken from 'components/shared/ExpiredToken';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import * as idb from 'utils/shared/idbUrl';
import { useGetPage } from 'hooks/useGetPage';

import { addDays, saveScreenshot, showWarning } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import TicketDetailsOptions from './TicketDetailsOptions';
import TicketDetailsDeal from './TicketDetailsDeal';
import Button from 'components/ui/Button/Button';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';

import { faArrowCircleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import usePackageDataLayer from 'hooks/usePackageDataLayer';
import { b2b, isNetravel } from 'constants/generic';

const DetailsPackagePage: FC = () => {
  const { t, i18n } = useTranslation();
  const [urlParams] = useSearchParams();
  const [response, setResponse] = useState<PackageDetailsResponse>();
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalAvgPrice, setTotalAvgPrice] = useState(0);
  const navigate = useNavigate();
  const detailsRef = useRef<HTMLDivElement>(null);
  const [flightIndex, setFlightIndex] = useState(0);
  const [flightOptIndex, setFlightOptIndex] = useState(0);
  const [outbound, setOutbound] = useState<OutboundModel>();
  const [inbound, setInbound] = useState<OutboundModel>();
  const [addOptFlights, setAddOptFlights] = useState<Select[]>();
  const [addFlights, setAddFlights] = useState<Select[]>();
  
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  const [rooms, setRooms] = useState<string[]>([]);
  const [infants, setInfants] = useState<string[]>([]);
  const [infantsCount, setInfantsCount] = useState(0);
  const cmsPage = useGetPage('ILPackages/Details');
  const stateRef = useRef<any>();
  const { pushPackageProductDetails } = usePackageDataLayer();
  stateRef.current = {
    response,
    totalPrice,
    totalAvgPrice,
    flightIndex,
    flightOptIndex,
    rooms,
    infants,
    infantsCount,
  };

  const { onClickBack, onClickSearch } = usePageInMemory(idbKey, () => saveState());

  useEffect(() => {
    if (b2b) {
      if (location.pathname.toLowerCase() !== `/ilpackage/details`) {
        return;
      }
    }
    (async () => {
      const savedState = await idb.getPageState(idbKey);

      if (savedState && savedState.response) {
        setResponse(savedState.response);
        setTotalPrice(savedState.totalPrice);
        setTotalAvgPrice(savedState.totalAvgPrice);
        setFlightIndex(savedState.flightIndex);
        setFlightOptIndex(savedState.flightOptIndex);
        setRooms(savedState.rooms);
        setInfants(savedState.infants);
        setInfantsCount(savedState.infantsCount);

        idb.deletePageState(idbKey);
      } else {
        const params = Object.fromEntries(urlParams);
        const body = new GetPackageDetailsRequest(params);
        const response = await getPackageDetails(body);

        if (response?.dealModel?.redirectToSearchAfterSale) {
          const adults = (body.paxComplect.toLowerCase().match(/a/g) || []).length;
          const children = (body.paxComplect.toLowerCase().match(/c/g) || []).length;
          const newParams = {
            checkIn: body.checkIn,
            checkOut: dateFormat(
              addDays(params.nights ? +params.nights : 0, new Date(body.checkIn)),
              'yyyy-MM-dd'
            ),
            cityCode: body.cityCode + '-0',
            type: body.type + '',
            roomType: `${adults};${children}`,
            flightSourcesDefault: 'ALP',
          };

          const search = new URLSearchParams(newParams).toString();

          navigate({ pathname: '/ILPackages/Results', search });
        } else if (response) {
          setResponse(response);

          if (response?.dealModel) {
            pushPackageProductDetails(response.dealModel);
            const df = 'EEE - MMM dd, yyyy - HH:mm';
            const { flights, options } = response.dealModel;
            if (flights?.length) {
              setInbound(flights[0].inbound[0]);
              setOutbound(flights[0].outbound[0]);

              if (flights.length > 1) {
                const newoptions = flights.map((fl, index) => ({
                  value: index,
                  label: `${fl.outbound[0].airline} ${dateFormat(
                    fl.outbound[0].date + ' ' + fl.outbound[0].depTime,
                    df
                  )} - ${fl.inbound[0].airline} ${dateFormat(
                    fl.inbound[0].date + ' ' + fl.inbound[0].depTime,
                    df
                  )}`,
                }));
                setAddFlights(newoptions);
              }
            } else if (options?.[0]?.flights?.length) {
              setInbound(options[0].flights[0].inbound);
              setOutbound(options[0].flights[0].outbound);

              if (options[0].flights.length > 1) {
                const newoptions = options[0].flights.map((fl, index) => {
                  return {
                    value: index,
                    label: `${fl.outbound.airline} ${dateFormat(
                      fl.outbound.date + ' ' + fl.outbound.depTime,
                      df
                    )} - ${fl.inbound.airline} ${dateFormat(
                      fl.inbound.date + ' ' + fl.inbound.depTime,
                      df
                    )}`,
                  };
                });
                setAddOptFlights(newoptions);
              }
            }
          }
        } else if (!response) {
          setResponse({ hasErrors: true, errorMessage: 'Something went wrong', } as PackageDetailsResponse);
        }
      }
    })();
  }, [urlParams, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangePax = (
    totalPrice: number,
    totalAvgPrice: number,
    rooms: string[],
    infants: string[],
    infantsCount: number
  ) => {
    setTotalPrice(totalPrice);
    setTotalAvgPrice(totalAvgPrice);
    setRooms(rooms);
    setInfants(infants);
    setInfantsCount(infantsCount);
  };

  const onClickBook = () => {
    if (rooms.length) {
      saveScreenshot(detailsRef.current, 'Details', 'Packages');
      const additionalParams = new URLSearchParams(rooms.map((room) => ['selectedRooms', room]));

      if (infantsCount) {
        additionalParams.append('infants', infantsCount.toString());
      }

      if (infants.length) {
        infants.forEach((infant) => {
          additionalParams.append('selectedInfantInRooms', infant);
        });
      }

      additionalParams.append('flightOptIndex', flightOptIndex.toString());
      additionalParams.append('flightIndex', flightIndex.toString());
      additionalParams.append('searchToken', response?.searchToken || '');
      const urlParamsStr = urlParams.toString() + '&' + additionalParams.toString();

      navigate({ pathname: '/ilpackage/book', search: urlParamsStr });
      saveState();
    } else {
      showWarning(t('hotel.select_rooms'));
    }
  };

  const saveState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  return (
    <div className="details-wrapper package-details-wrapper" ref={detailsRef}>
      {!!response &&
        !response?.hasErrors &&
        !response.sessionExpired &&
        !!response.dealModel?.info?.hotelName && (
          <div className="flex-column w100p">
            {window.history.length > 1 && (
              <div className="mb-20 default-shadow flex-between dyn-change-header">
                <Button
                  title={t('back')}
                  icon={faArrowCircleLeft}
                  onClick={() => onClickBack('ilpackage', 'result')}
                  className="btn btn-link"
                />
                <Button
                  title={t('search.new')}
                  icon={faSearch}
                  onClick={() => onClickSearch('ilpackage', '/ilpackages')}
                  className="btn btn-link"
                />
              </div>
            )}
            <div className="flex-between w100p">
              <div className="details package-details">
                <PackageDetails
                  dealModel={response.dealModel}
                  totalAvgPrice={totalAvgPrice}
                  totalPrice={totalPrice}
                  outbound={outbound}
                  inbound={inbound}
                  onClickBook={onClickBook}
                />

                {!!addOptFlights?.length && (
                  <div className="default-shadow flight-details-item">
                    <div className="mb-10">{t('package.please_select_flight')}</div>
                    <SelectCustom
                      options={addOptFlights}
                      value={flightOptIndex}
                      onChange={(val) => {
                        setInbound(response.dealModel.options[0].flights[val].inbound);
                        setOutbound(response.dealModel.options[0].flights[val].outbound);
                        setFlightOptIndex(val);
                      }}
                    />
                  </div>
                )}

                {!!addFlights?.length && (
                  <div className="default-shadow flight-details-item">
                    <div className="mb-10">{t('package.please_select_flight')}</div>
                    <SelectCustom
                      options={addFlights}
                      value={flightIndex}
                      onChange={(val) => {
                        setInbound(response.dealModel.flights[flightIndex].inbound[0]);
                        setOutbound(response.dealModel.flights[flightIndex].outbound[0]);
                        setFlightIndex(val);
                      }}
                    />
                  </div>
                )}
                {!!response.dealModel?.options?.[0]?.flights?.length && (
                  <Fragment>
                    <TicketDetailsOptions
                      type="departure"
                      bound={response.dealModel.options[0].flights[flightOptIndex].outbound}
                    />
                    <TicketDetailsOptions
                      type="return"
                      bound={response.dealModel.options[0].flights[flightOptIndex].inbound}
                    />
                  </Fragment>
                )}
                {!!response.dealModel?.flights?.length && (
                  <Fragment>
                    <TicketDetailsDeal
                      type="departure"
                      bound={response.dealModel.flights[flightIndex].outbound?.[0]}
                      pair={response.dealModel.flights[flightIndex].pairs?.[0]}
                    />
                    <TicketDetailsDeal
                      type="return"
                      bound={response.dealModel.flights[flightIndex].inbound?.[0]}
                      pair={response.dealModel.flights[flightIndex].pairs?.[0]}
                    />
                  </Fragment>
                )}
                {!isNetravel && <AdditionalDetails dealModel={response.dealModel} />}
                <HotelDetails
                  dealModel={response.dealModel}
                  onChangePax={onChangePax}
                  onClickBook={onClickBook}
                  totalPrice={totalPrice}
                  rooms={rooms}
                  infants={infants}
                  cmsPage={cmsPage}
                />
              </div>
              <SummaryDetails
                recommended={response.recommendedDeals}
                mapUrl={response.googleMapOfAirports}
              />
            </div>
          </div>
        )}

      {response?.sessionExpired && <ExpiredToken storePath="ilpackage" />}

      {(response?.hasErrors ||
        (!response?.dealModel?.info?.hotelName && !!response && !response.sessionExpired)) && (
          <div className="expired">
            <div className="title">{t('something_went_wrong')}</div>
            <p className="expired-content">{t('package.sorry_we_couldnt_locate_item')}</p>
            <Link to={'/'} className="btn btn-primary">
              {t('home.go_back')}
            </Link>
          </div>
        )}
      {!response && <DetailsPackageSkeleton />}
    </div>
  );
};

export default withErrorBoundary(DetailsPackagePage, 'DetailsPackagePage');
